/* eslint-disable no-nested-ternary, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SubmitButton from '../../common/SubmitButton';
import LoadingSpinner from '../../common/LoadingSpinner';
import { getEstimatedPrice, postPaymentAsync, setDiscount } from '../../../actions/BookingRequest';
import { checkValidVoucherCode } from '../../../actions/ClientData';
import { LANDING_PAGE_URL, SERVER_URL } from '../../../config';
import Summary from './Summary';
import PaymentDropdown from './PaymentDropdown';
import AddPaymentContainer from '../../payments/addpayment/addPaymentContainer';
import history from '../../../history';
import { TRootState } from '../../../reducers';
import StripePayment from './StripePayment';
import { TJobItem, TPaymentMethod } from '../../../types';


type TPaymentProps = {
    className: string;
}

export default function Payment({
    className,
}: TPaymentProps) {

    const dispatch = useDispatch();

    const [submitAllowed, setSubmitAllowed] = useState(false);
    const [voucherCode, setVoucherCode] = useState('');
    const [showVoucher, setShowVoucher] = useState(false);
    const [voucherValid, setVoucherValid] = useState(null);
    const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);
    const [addPayment] = useState('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [mode, setMode] = useState('default');

    const {
        jobRequests,
        loading,
        paymentMethods,
    } = useSelector((state: TRootState) => ({
        jobRequests: state.requestData.jobRequest,
        loading: state.loading.booking,
        paymentMethods: state.clientData.paymentMethods,
    }));

    const toggleVoucher = () => setShowVoucher(!showVoucher);

    const clearVoucher = () => {
        setVoucherCode('');
        setVoucherValid(null);
        dispatch(setDiscount(null));
        setShowVoucher(false);
    };

    const onVoucherInputBlur = (voucher: string) => {
        setVoucherValid(null);

        if (voucher && voucher.length) {
            dispatch(checkValidVoucherCode(voucher, (response: any, error: any) => {
                if (response && !error) {
                    const jobIds = jobRequests.map((job: TJobItem) => job.id);
                    dispatch(getEstimatedPrice(voucher, jobIds));
                    setVoucherValid(true);
                } else {
                    setVoucherValid(false);
                    dispatch(setDiscount(null));
                }
            }));
        }
    };

    useEffect(() => () => clearVoucher(), []);

    const onPaymentMethodSelect = (newPaymentMethod: TPaymentMethod) => {
        if (newPaymentMethod) {
            setSelectedPaymentMethod(newPaymentMethod);
            setSubmitAllowed(true);
        }
    };

    const onModalDismiss = () => {
        setShowNewPaymentModal(false);
    };

    const onClickCancel = (e: any) => {
        e.preventDefault();
        if (jobRequests[0].series) {
            history.push(`/jobs/list/?series=${jobRequests[0].series}&getall=1&page=1`);
        } else {
            window.location.assign(`${SERVER_URL}/job/${jobRequests[0].id}`);
        }
    };

    const submitPayment = (payload: any) => {
        dispatch(postPaymentAsync(payload, (response: any, error: any) => {
            if (response && !error)
                window.location = response.content.redirect_uri;
            if (error) setSubmitAllowed(true);
        }));
    };

    const onClickSubmit = (e: any) => {
        e.preventDefault();

        setSubmitAllowed(false);

        if (selectedPaymentMethod && selectedPaymentMethod.id) {
            const jobIds = jobRequests.map((job: TJobItem) => job.id);
            const payload = {
                jobrequests: jobIds,
                payment_method: selectedPaymentMethod.id,
                voucher: voucherCode,
            };
            submitPayment(payload);
        } else {
            setSubmitAllowed(true);
        }
    };

    const voucherSection = () => (
        <div className="voucher-wrapper">
            <input
                className="voucher-code"
                placeholder="Type your code here if you have one"
                value={voucherCode}
                data-field="voucherCode"
                onChange={e => setVoucherCode(e.target.value)}
                onBlur={e => onVoucherInputBlur(e.target.value)}
            />
            {voucherValid === true && (
                <p className="voucher-result valid">Voucher applied!</p>
            )}
            {voucherValid === false && (
                <p className="voucher-result invalid">Invalid code</p>
            )}
            <p className="voucher-clear" onClick={clearVoucher}>
                <i className="icon icon-close2" />
            </p>
        </div>
    );

    const renderPaymentInstructions = (option: any) =>
        option.provider === 'banktransfer' ? (
            <p className="payment-instruction">
                This booking will be due after completion. You will receive a weekly
                invoice covering the costs, that will need to be settled by bank
                transfer.
            </p>
        ) : option.provider === 'stripe' && jobRequests?.length ? (
            <StripePayment
                mode={mode}
                setMode={setMode}
                jobrequests={jobRequests}
                paymentMethod={option}
                voucherCode={voucherCode}
                voucherValid={voucherValid}
                onCancel={onClickCancel}
            />
        ) : null;

    return (
        <div className={className}>
            <div className="content-wrapper">

                {loading
                    ? <LoadingSpinner className="small" />
                    : mode !== 'complete'
                        ?
                            <Summary
                                voucherSection={voucherSection()}
                                showVoucher={showVoucher}
                                toggleVoucher={toggleVoucher}
                            />
                        : <></>
                }

                <div className={className}>
                    {!paymentMethods
                        ? <LoadingSpinner />
                        : mode !== 'complete'
                            ?
                                <div>
                                    <p className="select-instruction">Select a payment method for this booking:</p>
                                    <PaymentDropdown
                                        enableSetLater={false}
                                        enableAddStripe={true}
                                        onPaymentMethodSelect={onPaymentMethodSelect}
                                    />
                                </div>
                            : <></>
                    }
                    {selectedPaymentMethod && renderPaymentInstructions(selectedPaymentMethod)}
                </div>

                {selectedPaymentMethod?.provider !== 'stripe' && (
                    <>
                        <p className="option-secondary">
                            By clicking on &quot;Confirm Booking&quot;, you agree to the{' '}
                            <a
                                target="_blank"
                                className="nowrap-white-space"
                                href={`${LANDING_PAGE_URL}/legal`}
                                rel="noreferrer"
                            >
                                service contract
                            </a>
                            .
                        </p>
                        <SubmitButton
                            onClick={onClickSubmit}
                            propName="postPayment"
                            className={`btn-confirm-job${submitAllowed ? ' active' : ''}`}
                        >
                            CONFIRM BOOKING
                        </SubmitButton>
                        <button
                            className="btn-payment-cancel"
                            onClick={onClickCancel}
                            type="button"
                        >
                            CANCEL
                        </button>
                    </>
                )}
            </div>
            <AddPaymentContainer
                mode={addPayment}
                paymentMethod={selectedPaymentMethod}
                isOpen={showNewPaymentModal}
                onRequestClose={onModalDismiss}
            />
        </div>
    );
}
